import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  CreditCard,
  UserCheck,
  Receipt,
  CircleDollarSign
} from "lucide-react";
import {
  getStudentFeeLedger,
  getDefaulters
} from "../../api/fees/feeApi";
import {
  getAllStudents,
  getAllClasses
} from "../../api/student/studentApi";
import CollectFeeModal from "../../components/fees/CollectFeeModal";
import FeeReceiptModal from "../../components/fees/FeeReceiptModal";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Badge,
  Checkbox,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const studentDisplayName = (s) =>
  s.name || s.fullName || [s.firstName, s.middleName, s.lastName].filter(Boolean).join(" ") || "—";

const FeeCollectionDashboard = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterClassId, setFilterClassId] = useState("");
  const [classes, setClasses] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [student, setStudent] = useState(null);
  const [dues, setDues] = useState([]);
  const [selectedDues, setSelectedDues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDues, setLoadingDues] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const toast = useToast();

  // Load Classes on mount
  useEffect(() => {
    getAllClasses()
      .then((data) => setClasses(data || []))
      .catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() && !filterClassId) {
      toast.error("Enter a name/roll or select a class.");
      return;
    }
    setLoading(true);
    setStudent(null);
    setDues([]);
    setSelectedDues([]);
    setSearchResults([]);
    try {
      const filters = {};
      if (filterClassId) filters.classId = filterClassId;
      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
        filters.name = searchQuery.trim();
      }
      const data = await getAllStudents(filters);
      const list = Array.isArray(data) ? data : data?.data || [];
      setSearchResults(list);
      if (list.length === 0) {
        toast.error("No students found.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Search failed.");
    } finally {
      setLoading(false);
    }
  };

  const selectStudent = async (stu) => {
    setStudent(stu);
    setSearchResults([]);
    setSelectedDues([]);
    setLoadingDues(true);
    try {
      const res = await getStudentFeeLedger(stu._id);
      const hist = res?.data || res || {};
      const pending =
        hist.pending ||
        hist.dues ||
        hist.pendingFees ||
        hist.ledger?.filter?.(
          (row) =>
            Number(row.balance || 0) > 0 ||
            row.status === "PENDING" ||
            row.status === "PARTIAL"
        ) ||
        [];
      setDues(Array.isArray(pending) ? pending : []);
    } catch (err) {
      try {
        const dueRes = await getDefaulters({ studentId: stu._id });
        const dueList = dueRes?.data || dueRes || [];
        setDues(Array.isArray(dueList) ? dueList : []);
      } catch {
        toast.error(err.response?.data?.message || "Unable to load dues.");
        setDues([]);
      }
    } finally {
      setLoadingDues(false);
    }
  };

  const toggleDue = (due) => {
    setSelectedDues((prev) => {
      const id = due._id;
      if (prev.find((d) => d._id === id)) {
        return prev.filter((d) => d._id !== id);
      }
      return [...prev, due];
    });
  };

  const toggleAllDues = () => {
    if (selectedDues.length === dues.length) {
      setSelectedDues([]);
    } else {
      setSelectedDues([...dues]);
    }
  };

  const handleCollectSuccess = (receipt) => {
    toast.success("Payment collected successfully.");
    setReceiptData(receipt);
    setReceiptOpen(true);
    setSelectedDues([]);
    if (student?._id) {
      selectStudent(student);
    }
  };

  // Option Derivations
  const classOptions = useMemo(
    () => [
      { label: "All Classes", value: "" },
      ...classes.map((cls) => ({
        label: `Class ${cls.className}`,
        value: cls._id,
      })),
    ],
    [classes]
  );

  // Computed summary for selected dues
  const selectedTotal = useMemo(() => {
    return selectedDues.reduce(
      (sum, d) => sum + Number(d.balance || d.amount || 0),
      0
    );
  }, [selectedDues]);

  const tableRows = useMemo(
    () =>
      dues.map((d) => ({
        ...d,
        id: d._id,
        headName: d.feeHeadName || d.feeHeadId?.name || d.name || "—",
        formattedAmount: Number(d.amount || 0).toFixed(2),
        formattedBalance: Number(d.balance || d.amount || 0).toFixed(2),
        formattedDueDate: d.dueDate
          ? new Date(d.dueDate).toLocaleDateString("en-IN")
          : "—",
        isSelected: !!selectedDues.find((s) => s._id === d._id),
      })),
    [dues, selectedDues]
  );

  const columns = [
    {
      key: "select",
      header: (
        <Checkbox
          checked={dues.length > 0 && selectedDues.length === dues.length}
          indeterminate={
            selectedDues.length > 0 && selectedDues.length < dues.length
          }
          onChange={toggleAllDues}
          aria-label="Select all dues"
        />
      ),
      render: (row) => (
        <Checkbox
          checked={row.isSelected}
          onChange={() => toggleDue(row)}
          aria-label={`Select due for ${row.headName}`}
        />
      ),
    },
    {
      key: "headName",
      header: "Fee Head",
      render: (row) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">{row.headName}</span>
      ),
    },
    {
      key: "formattedAmount",
      header: "Amount",
      align: "right",
      render: (row) => `₹${row.formattedAmount}`,
    },
    {
      key: "formattedBalance",
      header: "Balance",
      align: "right",
      render: (row) => (
        <span className="font-medium text-danger-500">
          ₹{row.formattedBalance}
        </span>
      ),
    },
    {
      key: "formattedDueDate",
      header: "Due Date",
      render: (row) => row.formattedDueDate,
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
          Fee Collection Dashboard
        </h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Search students, review pending dues, and process quick payments.
        </p>
      </div>

      {/* Alerts */}
      
      

      {/* Search Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="grid gap-3 sm:grid-cols-3 items-end">
            <div className="sm:col-span-1">
              <Input
                placeholder="Name, roll no, or admission no..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>
            <div>
              <Select
                options={classOptions}
                value={filterClassId}
                onChange={(val) => setFilterClassId(val)}
              />
            </div>
            <div>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={loading}
                leftIcon={<Search className="h-4 w-4" />}
              >
                Search
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Search Results Picker */}
      {searchResults.length > 0 && (
        <Card>
          <CardHeader className="bg-neutral-50/50 py-3">
            <CardTitle className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Select Student ({searchResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-60 overflow-y-auto divide-y">
            {searchResults.map((stu) => (
              <button
                key={stu._id}
                type="button"
                onClick={() => selectStudent(stu)}
                className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {studentDisplayName(stu)}
                  </p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                    Roll: {stu.rollNo || stu.admissionNo || "—"} · Class:{" "}
                    {stu.classId?.className || stu.className || "—"}
                  </p>
                </div>
                <UserCheck className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Selected Student Dues Panel */}
      {student && (
        <Card>
          <CardHeader className="flex flex-col gap-3 border-b bg-neutral-50/50 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                {studentDisplayName(student)}
              </CardTitle>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                Roll / Adm No:{" "}
                <span className="font-medium text-neutral-700 dark:text-neutral-200">
                  {student.rollNo || student.admissionNo || "—"}
                </span>{" "}
                · Class:{" "}
                <span className="font-medium text-neutral-700 dark:text-neutral-200">
                  {student.classId?.className
                    ? `Class ${student.classId.className}`
                    : student.className
                    ? `Class ${student.className}`
                    : "—"}
                </span>
              </p>
            </div>

            {selectedDues.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="text-xs text-neutral-500 dark:text-neutral-400 block">Selected Total</span>
                  <span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    ₹{selectedTotal.toFixed(2)}
                  </span>
                </div>
                <Button
                  variant="primary"
                  onClick={() => setCollectOpen(true)}
                  leftIcon={<CreditCard className="h-4 w-4" />}
                >
                  Collect ({selectedDues.length})
                </Button>
              </div>
            )}
          </CardHeader>

          <CardContent className="p-0">
            {loadingDues ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex gap-4">
                    <Skeleton height={16} width="10%" />
                    <Skeleton height={16} width="35%" />
                    <Skeleton height={16} width="20%" />
                    <Skeleton height={16} width="20%" />
                  </div>
                ))}
              </div>
            ) : (
              <Table
                columns={columns}
                data={tableRows}
                emptyState={
                  <EmptyState
                    icon={CircleDollarSign}
                    title="No pending dues"
                    description="This student has paid all outstanding balances."
                  />
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Collect Fee Action Modal */}
      <CollectFeeModal
        isOpen={collectOpen}
        onClose={() => setCollectOpen(false)}
        onSuccess={handleCollectSuccess}
        student={student}
        selectedDues={selectedDues}
      />

      {/* Fee Receipt Display Modal */}
      <FeeReceiptModal
        isOpen={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        receipt={receiptData}
      />
    </div>
  );
};

export default FeeCollectionDashboard;